import { useMemo } from 'react';

import {
  useGetMyTaskWorktimeQuery,
  useMyTotalFrameStateQuery,
  useMyTotalTaskStateQuery,
  useMyWorkTotalObjectQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  History,
  RateReview,
  RestartAlt,
  Schedule,
  TaskAlt,
  VFlex,
  VGrid,
  VStack,
  VideoLabel,
} from '@/package';
import type { TaskStateStatistic } from '@/type';
import { makeAvgTime } from '@/util';

import { StatisticSummaryCard, calculateTotalObject } from '../Statistic';
import { DoughnutChart } from '../Statistic/DoughnutChart';

export const MyWorkProjectStatisticSummary = () => {
  const { data: totalFrameState } = useMyTotalFrameStateQuery();
  const { data: totalTaskState } = useMyTotalTaskStateQuery();
  const { data: projectObject } = useMyWorkTotalObjectQuery(true);
  const { data: myTaskWorktime } = useGetMyTaskWorktimeQuery();

  const totalTaskCount = totalTaskState?.total ?? 0;
  const totalObjectCount = calculateTotalObject(projectObject?.statExtendClass);
  const totalFrameCount = totalFrameState?.total ?? 0;
  const taskProgress = {
    none: (totalTaskState as TaskStateStatistic)?.none ?? 0,
    pending: (totalTaskState as TaskStateStatistic)?.pending ?? 0,
    ready: (totalTaskState as TaskStateStatistic)?.ready ?? 0,
    // @ts-expect-error - TASK_WORKSTATE.PROGRESS is 'In Progress'
    progress: (totalTaskState as TaskStateStatistic)?.['in Progress'] ?? 0,
    completed: (totalTaskState as TaskStateStatistic)?.completed ?? 0,
    fail: (totalTaskState as TaskStateStatistic)?.fail ?? 0,
  };

  const avgTime = useMemo(() => {
    if (!myTaskWorktime?.[0]) {
      return {
        work: '0m 0s',
        review: '0m 0s',
        rework: '0m 0s',
      };
    }

    return makeAvgTime(myTaskWorktime[0]);
  }, [myTaskWorktime]);

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Summary</div>

      <VGrid cols={4} gap={1}>
        <StatisticSummaryCard
          Icon={TaskAlt}
          content={() => Intl.NumberFormat().format(totalTaskCount)}
          title="Total Tasks"
        />

        <StatisticSummaryCard
          Icon={VideoLabel}
          content={() => Intl.NumberFormat().format(totalFrameCount)}
          title="Total Work Frames"
        />

        <VStack
          gap={0.5}
          className="col-span-2 row-span-2 rounded-md px-4 py-2 shadow-sm"
        >
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total tasks progress
            </VFlex>

            <div className="text-body-sm text-gray-600">
              Total {totalTaskCount}
            </div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={totalTaskCount}
              taskProgress={taskProgress}
            />
          </VFlex>
        </VStack>

        <StatisticSummaryCard
          Icon={CenterFocusWeak}
          content={() => Intl.NumberFormat().format(totalObjectCount)}
          title="Total Objects"
        />

        <div className="flex-1">&nbsp;</div>

        <StatisticSummaryCard
          Icon={Schedule}
          content={() => avgTime.work}
          title="Avg Work Time"
        />

        <StatisticSummaryCard
          Icon={RateReview}
          content={() => avgTime.review}
          title="Avg Review Time"
        />

        <StatisticSummaryCard
          Icon={RestartAlt}
          content={() => avgTime.rework}
          title="Avg Rework Time"
        />
      </VGrid>
    </VStack>
  );
};

MyWorkProjectStatisticSummary.displayName = 'MyWorkProjectStatisticSummary';
